import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import NeonWordmark from '../components/NeonWordmark'

interface KnockoutArtist {
  id: string
  slug: string
  label: string
  safeTag: string
  tone: string
  imageUrl: string | null
  seedOrder: number
}

interface KnockoutPick {
  id: string
  pickIndex: number
  stage: string
  selected: KnockoutArtist | null
  eliminated: KnockoutArtist | null
}

interface KnockoutState {
  session: { id: string; status: string } | null
  player: { displayName: string | null; profileImageUrl: string | null } | null
  canManage: boolean
  picks: KnockoutPick[]
  progress: {
    completed: number
    total: number
    progressLabel: string
    stage: string
    stageLabel: string
  }
  currentMatchup: {
    pickIndex: number
    stage: string
    progressLabel: string
    stageLabel: string
    artists: [KnockoutArtist, KnockoutArtist]
  } | null
  winner: KnockoutArtist | null
}

interface KnockoutResponse {
  state?: KnockoutState
  error?: string
  warning?: string
}

const DEMO_ROSTER: KnockoutArtist[] = [
  { id: 'demo-01', slug: 'hernan-cattaneo', label: 'Hernán Cattáneo', safeTag: 'Progressive house', tone: '#5A7282', imageUrl: '/knockout-artists/hernan-cattaneo.jpeg', seedOrder: 1 },
  { id: 'demo-02', slug: 'nick-warren', label: 'Nick Warren', safeTag: 'Progressive', tone: '#3D5665', imageUrl: '/knockout-artists/nick-warren.jpg', seedOrder: 2 },
  { id: 'demo-03', slug: 'swedish-house-mafia', label: 'Swedish House Mafia', safeTag: 'Big-room group', tone: '#B04E2B', imageUrl: '/knockout-artists/swedish-house-mafia.jpg', seedOrder: 3 },
  { id: 'demo-04', slug: 'hardwell', label: 'Hardwell', safeTag: 'Big-room', tone: '#7A3820', imageUrl: '/knockout-artists/hardwell.jpg', seedOrder: 4 },
  { id: 'demo-05', slug: 'guy-j', label: 'Guy J', safeTag: 'Progressive', tone: '#4A5644', imageUrl: '/knockout-artists/guy-j.jpg', seedOrder: 5 },
  { id: 'demo-06', slug: 'guy-mantzur', label: 'Guy Mantzur', safeTag: 'Progressive', tone: '#6B7A5E', imageUrl: '/knockout-artists/guy-mantzur.jpg', seedOrder: 6 },
  { id: 'demo-07', slug: 'eric-prydz', label: 'Eric Prydz', safeTag: 'Progressive', tone: '#3D5665', imageUrl: '/knockout-artists/eric-prydz.jpg', seedOrder: 7 },
  { id: 'demo-08', slug: 'tiesto', label: 'Tiësto', safeTag: 'Trance / open-format', tone: '#B85540', imageUrl: '/knockout-artists/tiesto.jpg', seedOrder: 8 },
  { id: 'demo-09', slug: 'solomun', label: 'Solomun', safeTag: 'Melodic house', tone: '#5C5348', imageUrl: '/knockout-artists/solomun.jpg', seedOrder: 9 },
  { id: 'demo-10', slug: 'avicii', label: 'Avicii', safeTag: 'Progressive', tone: '#8A6440', imageUrl: '/knockout-artists/avicii.jpg', seedOrder: 10 },
  { id: 'demo-11', slug: 'tale-of-us', label: 'Tale of Us', safeTag: 'Afterlife', tone: '#8F6B8E', imageUrl: '/knockout-artists/tale-of-us.jpg', seedOrder: 11 },
  { id: 'demo-12', slug: 'deadmau5', label: 'deadmau5', safeTag: 'Progressive house', tone: '#3D5665', imageUrl: '/knockout-artists/deadmau5.jpg', seedOrder: 12 },
  { id: 'demo-13', slug: 'black-coffee', label: 'Black Coffee', safeTag: 'Afro-house', tone: '#4A2E1A', imageUrl: '/knockout-artists/black-coffee.jpg', seedOrder: 13 },
  { id: 'demo-14', slug: 'carl-cox', label: 'Carl Cox', safeTag: 'Techno', tone: '#7A3820', imageUrl: '/knockout-artists/carl-cox.jpg', seedOrder: 14 },
  { id: 'demo-15', slug: 'martin-garrix', label: 'Martin Garrix', safeTag: 'Big-room', tone: '#B04E2B', imageUrl: '/knockout-artists/martin-garrix.jpg', seedOrder: 15 },
  { id: 'demo-16', slug: 'boris-brejcha', label: 'Boris Brejcha', safeTag: 'High-tech minimal', tone: '#2E2B26', imageUrl: '/knockout-artists/boris-brejcha.jpg', seedOrder: 16 },
]

function stageInfoForPick(pickIndex: number) {
  if (pickIndex <= 8) return { stage: 'opening', progressLabel: `${String(pickIndex).padStart(2, '0')} / 08`, stageLabel: 'Opening round' }
  if (pickIndex <= 12) return { stage: 'quarter', progressLabel: `Quarter-final ${pickIndex - 8} / 4`, stageLabel: 'Quarter-final' }
  if (pickIndex <= 14) return { stage: 'semi', progressLabel: `Semi-final ${pickIndex - 12} / 2`, stageLabel: 'Semi-final' }
  return { stage: 'final', progressLabel: 'Final', stageLabel: 'Final' }
}

function previewPairForPick(pickIndex: number, picks: KnockoutPick[]): [KnockoutArtist, KnockoutArtist] | null {
  if (pickIndex <= 8) {
    const offset = (pickIndex - 1) * 2
    const pair = [DEMO_ROSTER[offset], DEMO_ROSTER[offset + 1]]
    return pair[0] && pair[1] ? pair as [KnockoutArtist, KnockoutArtist] : null
  }
  const winners = picks.map(pick => pick.selected).filter((artist): artist is KnockoutArtist => Boolean(artist))
  const base = pickIndex <= 12 ? 0 : pickIndex <= 14 ? 8 : 12
  const offset = (pickIndex <= 12 ? pickIndex - 9 : pickIndex <= 14 ? pickIndex - 13 : 0) * 2
  const pair = [winners[base + offset], winners[base + offset + 1]]
  return pair[0] && pair[1] ? pair as [KnockoutArtist, KnockoutArtist] : null
}

function previewStateFromPicks(picks: KnockoutPick[], status: 'ready' | 'in_progress' | 'completed' = 'in_progress'): KnockoutState {
  const completed = Math.min(picks.length, 15)
  const winner = picks.find(pick => pick.pickIndex === 15)?.selected ?? null
  const nextPickIndex = completed + 1
  const stageInfo = nextPickIndex <= 15 ? stageInfoForPick(nextPickIndex) : null
  const pair = stageInfo && status !== 'ready' ? previewPairForPick(nextPickIndex, picks) : null

  return {
    session: { id: 'local-preview', status: winner ? 'completed' : status },
    player: { displayName: 'Demo member', profileImageUrl: null },
    canManage: true,
    picks,
    progress: {
      completed,
      total: 15,
      progressLabel: status === 'ready' ? 'Ready' : stageInfo?.progressLabel ?? 'Complete',
      stage: status === 'ready' ? 'ready' : stageInfo?.stage ?? 'complete',
      stageLabel: status === 'ready' ? 'Ready' : stageInfo?.stageLabel ?? 'Complete',
    },
    currentMatchup: pair && stageInfo ? {
      pickIndex: nextPickIndex,
      stage: stageInfo.stage,
      progressLabel: stageInfo.progressLabel,
      stageLabel: stageInfo.stageLabel,
      artists: pair,
    } : null,
    winner,
  }
}

const DEMO_READY_STATE = previewStateFromPicks([], 'ready')

function initials(name?: string | null) {
  return (name || 'DK').split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()
}

function ArtistImage({ artist }: { artist: KnockoutArtist }) {
  const [failed, setFailed] = useState(false)
  if (!artist.imageUrl || failed) return <div className="knockout-card-poster" aria-hidden="true" />
  return <img src={artist.imageUrl} alt="" onError={() => setFailed(true)} />
}

function sameMatchup(previous: KnockoutState | null, next: KnockoutState | null) {
  const previousMatchup = previous?.currentMatchup
  const nextMatchup = next?.currentMatchup
  return previous?.session?.id === next?.session?.id
    && previousMatchup?.pickIndex === nextMatchup?.pickIndex
    && previousMatchup?.artists[0]?.id === nextMatchup?.artists[0]?.id
    && previousMatchup?.artists[1]?.id === nextMatchup?.artists[1]?.id
}

function ArtistCard({ artist, side, selected, dimmed, locked, tabIndex, onSelect, onKeyDown }: {
  artist: KnockoutArtist
  side: 'A' | 'B'
  selected: boolean
  dimmed: boolean
  locked: boolean
  tabIndex: number
  onSelect: () => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
}) {
  return (
    <button
      type="button"
      className={`knockout-card${selected ? ' selected' : ''}${dimmed ? ' dimmed' : ''}${locked ? ' locked' : ''}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      disabled={locked}
      tabIndex={tabIndex}
      role="radio"
      aria-checked={selected}
      aria-label={`${artist.label}${selected ? ', selected' : ''}`}
      style={{ '--artist-tone': artist.tone } as CSSProperties}
    >
      <div className="knockout-card-media">
        <ArtistImage artist={artist} />
        <span className="knockout-side">{side}</span>
        {selected && <span className="knockout-survives">Survives</span>}
      </div>
      <div className="knockout-card-copy">
        <h2>{artist.label}</h2>
        <p>{artist.safeTag}</p>
      </div>
    </button>
  )
}

export default function KnockoutPage() {
  const { isLoggedIn, loading } = useAuth()
  const devPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === '1'
  const [state, setState] = useState<KnockoutState | null>(null)
  const stateRef = useRef<KnockoutState | null>(null)
  const loadRequestRef = useRef(0)
  const mutationRef = useRef(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function applyState(nextState: KnockoutState | null) {
    stateRef.current = nextState
    setState(nextState)
  }

  async function loadSession(options: { preserveSelection?: boolean } = {}) {
    if (devPreview) {
      applyState(DEMO_READY_STATE)
      return
    }
    const requestId = loadRequestRef.current + 1
    loadRequestRef.current = requestId
    const mutationVersion = mutationRef.current
    const previousState = stateRef.current
    setError(null)
    try {
      const res = await fetch('/api/knockout/session', { credentials: 'include', cache: 'no-store' })
      const data = await res.json() as KnockoutResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load Knockout Session')
      if (requestId !== loadRequestRef.current || mutationVersion !== mutationRef.current) return
      const nextState = data.state ?? null
      applyState(nextState)
      setSelectedId(current => options.preserveSelection && sameMatchup(previousState, nextState) && nextState?.currentMatchup?.artists.some(artist => artist.id === current) ? current : null)
    } catch (err) {
      if (requestId !== loadRequestRef.current || mutationVersion !== mutationRef.current) return
      setError(err instanceof Error ? err.message : 'Unable to load Knockout Session')
    }
  }

  useEffect(() => {
    if (devPreview || isLoggedIn) void loadSession()
  }, [devPreview, isLoggedIn])

  useEffect(() => {
    if (devPreview || !isLoggedIn || !state?.session || busy) return undefined
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadSession({ preserveSelection: true })
    }, 5000)
    return () => window.clearInterval(timer)
  }, [busy, devPreview, isLoggedIn, state?.session?.id])

  async function begin(options: { restart?: boolean } = {}) {
    mutationRef.current += 1
    if (devPreview) {
      applyState(previewStateFromPicks([], 'in_progress'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/knockout/session', {
        method: 'POST',
        headers: options.restart ? { 'content-type': 'application/json' } : undefined,
        credentials: 'include',
        body: options.restart ? JSON.stringify({ action: 'restart' }) : undefined,
      })
      const data = await res.json() as KnockoutResponse
      if (!res.ok) throw new Error(data.error ?? (options.restart ? 'Unable to restart session' : 'Unable to start session'))
      applyState(data.state ?? null)
      setSelectedId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : options.restart ? 'Unable to restart session' : 'Unable to start session')
    } finally {
      setBusy(false)
    }
  }

  async function confirmPick() {
    if (!selectedId) return
    const matchup = state?.currentMatchup
    if (!matchup) return
    mutationRef.current += 1
    if (devPreview) {
      const winner = matchup.artists.find(artist => artist.id === selectedId) ?? matchup.artists[0]
      const eliminated = matchup.artists.find(artist => artist.id !== selectedId) ?? matchup.artists[1]
      const nextPicks = [
        ...(state?.picks ?? []),
        { id: `demo-pick-${matchup.pickIndex}`, pickIndex: matchup.pickIndex, stage: matchup.stage, selected: winner, eliminated },
      ]
      applyState(previewStateFromPicks(nextPicks, nextPicks.length >= 15 ? 'completed' : 'in_progress'))
      setSelectedId(null)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/knockout/pick', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ selectedArtistId: selectedId, pickIndex: matchup.pickIndex }),
      })
      const data = await res.json() as KnockoutResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to save pick')
      applyState(data.state ?? null)
      setSelectedId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save pick')
    } finally {
      setBusy(false)
    }
  }

  if (!devPreview && loading) return <div className="knockout-page"><p>Checking access...</p></div>
  if (!devPreview && !isLoggedIn) return <Navigate to="/signin" replace />

  const playerName = state?.player?.displayName || 'Member'
  const matchup = state?.currentMatchup
  const winner = state?.winner
  const ready = !state?.session || state.session.status === 'ready'
  const selectedArtist = matchup?.artists.find(artist => artist.id === selectedId)

  return (
    <main className="knockout-page" aria-label="Knockout Session">
      <section className="knockout-phone">
        <header className="knockout-topbar">
          <Link to="/" className="knockout-brand" aria-label="Go to Yan-Vada home">
            <NeonWordmark id="neon-knockout-brand" height={56} />
          </Link>
          <span>{state?.progress.progressLabel ?? 'Knockout'}</span>
        </header>

        {state?.session && !ready && (
          <div
            className="knockout-progress-dots"
            role="progressbar"
            aria-label="Knockout progress"
            aria-valuenow={state.progress.completed}
            aria-valuemin={0}
            aria-valuemax={state.progress.total}
            aria-valuetext={`${state.progress.completed} of ${state.progress.total} picks complete`}
          >
            {Array.from({ length: state.progress.total }, (_, index) => (
              <span
                key={index}
                className={index < state.progress.completed ? 'complete' : index === state.progress.completed && !winner ? 'current' : ''}
              />
            ))}
          </div>
        )}

        {error ? (
          <div className="knockout-state knockout-error" role="alert">
            <span className="knockout-error-mark">!</span>
            <h1>Lost the signal.</h1>
            <p>{error === 'knockout_migration_required' ? 'The Knockout database migration has not been applied yet.' : 'The session is paused. Reconnect and we will pick up where we left off.'}</p>
            <button className="btn btn-primary" type="button" onClick={() => void loadSession()}>Try again</button>
          </div>
        ) : winner ? (
          <div className="knockout-state knockout-reveal" aria-busy={busy} style={{ '--artist-tone': winner.tone } as CSSProperties}>
            <span className="knockout-eyebrow">The one that survives</span>
            <div className="knockout-winner-card">
              <ArtistImage artist={winner} />
              <span className="knockout-pick-note">{playerName}'s pick</span>
              <div>
                <h1>{winner.label}</h1>
                <p>{winner.safeTag}</p>
              </div>
            </div>
            <div className="knockout-history">
              {state?.picks.slice(-5).map(pick => (
                <span key={pick.id}>{pick.selected?.label} over {pick.eliminated?.label}</span>
              ))}
            </div>
            <div className="knockout-reveal-actions">
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void begin({ restart: true })}>{busy ? 'Restarting...' : 'Restart session'}</button>
            </div>
          </div>
        ) : ready ? (
          <div className="knockout-state knockout-ready">
            <div className="knockout-avatar">{state?.player?.profileImageUrl ? <img src={state.player.profileImageUrl} alt="" /> : initials(playerName)}</div>
            <span className="knockout-eyebrow">Knockout Session</span>
            <h1>{playerName}, your session is ready.</h1>
            <p>Two enter. One survives. Eight opening picks, then quarter-final, semi-final, and final.</p>
            <ul>
              <li><strong>One pick per matchup</strong><span>Confirm to lock it in.</span></li>
              <li><strong>No going back</strong><span>The loser is gone for good.</span></li>
              <li><strong>Final reveal</strong><span>The last one standing wins the show.</span></li>
            </ul>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void begin()}>{busy ? 'Starting...' : 'Begin'}</button>
          </div>
        ) : matchup ? (
          <div className="knockout-round" aria-busy={busy}>
            <div className="knockout-round-label">
              <span>{matchup.stageLabel}</span>
              <strong>{matchup.progressLabel}</strong>
            </div>
            <div className="knockout-card-stack" role="radiogroup" aria-label="Choose the artist that survives this matchup">
              {matchup.artists.map((artist, index) => (
                <ArtistCard
                  key={artist.id}
                  artist={artist}
                  side={index === 0 ? 'A' : 'B'}
                  selected={selectedId === artist.id}
                  dimmed={!!selectedId && selectedId !== artist.id}
                  locked={busy}
                  tabIndex={selectedId ? selectedId === artist.id ? 0 : -1 : index === 0 ? 0 : -1}
                  onSelect={() => setSelectedId(artist.id)}
                  onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
                    event.preventDefault()
                    const nextIndex = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? (index + matchup.artists.length - 1) % matchup.artists.length : (index + 1) % matchup.artists.length
                    setSelectedId(matchup.artists[nextIndex].id)
                    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus()
                  }}
                />
              ))}
              {!selectedId && <div className="knockout-vs" aria-hidden="true">vs</div>}
            </div>
            <div className="knockout-confirm">
              {selectedId ? (
                <>
                  <p role="status"><strong>{selectedArtist?.label}</strong> survives this one.</p>
                  <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void confirmPick()}>{busy ? 'Locked in...' : 'Confirm pick'}</button>
                </>
              ) : <p>Choose the one that survives.</p>}
            </div>
          </div>
        ) : (
          <div className="knockout-state knockout-error">
            <h1>Session is not ready.</h1>
            <p>The roster or previous picks need admin attention before the next matchup can load.</p>
            <button className="btn btn-secondary" type="button" onClick={() => void loadSession()}>Refresh</button>
          </div>
        )}
      </section>
    </main>
  )
}
